import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, InputAdornment
} from '@mui/material';
import { Add, Search, LocalHospital } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const hospitalTypes = ['government', 'private', 'trust', 'clinic', 'multispecialty'];

const Hospitals = () => {
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', registrationNumber: '', type: 'private',
    'contact.phone': '', 'contact.email': '',
    'address.city': '', 'address.state': ''
  });

  const load = async () => {
    try {
      const res = await api.get('/hospitals', { params: { search } });
      setHospitals(res.data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search]);

  const handleSubmit = async () => {
    try {
      const body = {
        name: form.name, registrationNumber: form.registrationNumber, type: form.type,
        contact: { phone: form['contact.phone'], email: form['contact.email'] },
        address: { city: form['address.city'], state: form['address.state'] }
      };
      await api.post('/hospitals', body);
      toast.success('Hospital onboarded successfully');
      setOpen(false);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to onboard hospital');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Hospitals</Typography>
          <Typography variant="body2" color="text.secondary">Manage hospital onboarding</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)} sx={{ borderRadius: 2 }}>
          Onboard Hospital
        </Button>
      </Box>

      <TextField
        placeholder="Search hospitals..." value={search} onChange={e => setSearch(e.target.value)}
        sx={{ mb: 3, maxWidth: 400 }} fullWidth size="small"
        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
      />

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {hospitals.map(h => (
            <Grid item xs={12} sm={6} md={4} key={h._id}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                    <Box sx={{ bgcolor: '#e3f2fd', p: 1, borderRadius: 1.5 }}>
                      <LocalHospital sx={{ color: '#1976d2' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>{h.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{h.registrationNumber}</Typography>
                    </Box>
                    <Chip
                      label={h.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={h.isActive ? 'success' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <Chip label={h.type} size="small" variant="outlined" />
                    <Chip label={`${h.address?.city}, ${h.address?.state}`} size="small" variant="outlined" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">{h.contact?.email}</Typography>
                  <Typography variant="body2" color="text.secondary">{h.contact?.phone}</Typography>
                  {h.totalBeds > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 2 }}>
                      <Typography variant="caption">Total Beds: <strong>{h.totalBeds}</strong></Typography>
                      <Typography variant="caption">Available: <strong>{h.availableBeds}</strong></Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
          {hospitals.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" textAlign="center" py={6}>No hospitals found</Typography>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Onboard New Hospital</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {[
              { label: 'Hospital Name', key: 'name' },
              { label: 'Registration Number', key: 'registrationNumber' },
              { label: 'Phone', key: 'contact.phone' },
              { label: 'Email', key: 'contact.email' },
              { label: 'City', key: 'address.city' },
              { label: 'State', key: 'address.state' }
            ].map(({ label, key }) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth label={label} size="small"
                  value={(form as any)[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Type" size="small" value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {hospitalTypes.map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ borderRadius: 2 }}>Onboard</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Hospitals;
